import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { LOGO_URL, productsData } from "../lib/api";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-forest-deep text-cream"
    >
      {/* Big CTA */}
      <div className="border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-gold">Let's Talk</div>
            <h2 className="font-serif font-light mt-6 text-5xl lg:text-7xl leading-[1.05] text-cream max-w-3xl">
              Sourcing premium Indian botanicals — delivered to your shore.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="footer-cta-inquire"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-forest-deep text-sm tracking-[0.2em] uppercase hover:bg-gold-light transition-colors duration-300"
            >
              Start an Inquiry
              <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {/* Columns */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 overflow-hidden flex items-center justify-center">
              <img src={LOGO_URL} alt="Gralon" className="h-full w-full object-contain scale-[1.3]" />
            </div>
            <div>
              <div className="font-serif text-2xl text-cream">Gralon Global</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-gold">
                Pure Origins, Global Reach
              </div>
            </div>
          </div>
          <p className="mt-6 text-cream/70 text-sm leading-relaxed max-w-md">
            A premium Indian exporter of psyllium husk, super seeds and Ayurvedic
            herbs — partnering with global brands across pharmaceutical, food and
            wellness sectors.
          </p>
        </div>

        <div className="md:col-span-2">
          <div className="label-eyebrow text-gold/80">Products</div>
          <ul className="mt-5 space-y-2 text-sm">
            {productsData.slice(0, 5).map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/products/${p.slug}`}
                  className="text-cream/80 hover:text-gold transition-colors duration-300"
                >
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="label-eyebrow text-gold/80">Resources</div>
          <ul className="mt-5 space-y-2 text-sm">
            <li>
              <Link to="/insights" className="text-cream/80 hover:text-gold transition-colors duration-300">
                Market Insights
              </Link>
            </li>
            <li>
              <Link to="/products" className="text-cream/80 hover:text-gold transition-colors duration-300">
                Full Catalog
              </Link>
            </li>
            <li>
              <Link to="/about" className="text-cream/80 hover:text-gold transition-colors duration-300">
                Quality
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="label-eyebrow text-gold/80">Get in Touch</div>
          <ul className="mt-5 space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Mail strokeWidth={1.5} className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
              <a href="mailto:info@gralonglobal.com" className="text-cream/85 hover:text-gold" data-testid="footer-email">
                info@gralonglobal.com
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone strokeWidth={1.5} className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
              <a href="tel:+917737671164" className="text-cream/85 hover:text-gold" data-testid="footer-phone">
                +91 77376 71164
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin strokeWidth={1.5} className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
              <span className="text-cream/85" data-testid="footer-address">
                13/2 Vinayak Nagar, Bohra Ganesh Ji Road,<br />
                Udaipur, Rajasthan, India
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-cream/60">
          <div data-testid="footer-copyright">© {new Date().getFullYear()} Gralon Global. All rights reserved.</div>
          <div className="tracking-[0.2em] uppercase text-gold/70">Pure Origins · Global Reach</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
